// ---- Weapon pose + equip over the shared rig contract ----
// The stance / attack-swing / weapon-seating system, MOVED verbatim from
// player.ts at rig unification so it drives ANY humanoid-contract rig
// (src/model/rig.ts) — the player and every humanoid NPC with hand sockets.
// All state rides the rig root's userData (weaponSpec/weaponMesh/weaponType/
// weaponArchetype/combatReady/attackT/attackStyle/attackFreeze), same keys
// player.ts always used, so combat.ts / net.ts / the studio keep reading the
// flags they know.
//
// THREE-but-DOM-free — safe for the headless server (which never calls it) and
// both clients. The rig handle is SPARSE: every joint write is guarded, so a
// simple humanoid without knee/torso pivots still works.

import * as THREE from "three";
import { buildItem, type ItemSpec, type WeaponSpec } from "../items/define.ts";
import { disposeGroup } from "../meshkit.ts";
import {
  ARCHETYPE_ANIM, UNARMED, MOVE_CLIPS, JOINT_CHANNELS, sampleChannel,
  type AttackClip, type StancePose, type Vec3,
} from "./clips.ts";
import type { WeaponArchetypeId } from "./archetypes.ts";
import {
  BLEND_TUNING, attackEnvelope, applyClipBlended, stanceChannels, captureStanceFrom, applyStanceBlended,
} from "./blend.ts";
import { mergeHold } from "./hold-seat.ts";
import type { RigHandle } from "../model/rig.ts";

/** The hold transform that seats a weapon in the fist (see WeaponSpec.hold). */
type Hold = WeaponSpec["hold"];

/** Which motion an attack plays. `"primary"` = the equipped weapon's archetype
 *  attack clip (or the unarmed jab when nothing is held); any other value is a
 *  shared move-clip key (content/moves `clip` → MOVE_CLIPS). Kept a string on the
 *  wire so remotes resolve `"primary"` against their OWN equipped weapon. */
export type AttackSel = "primary" | (string & {});

/** Resolve a swing selector to its clip on a given rig: `"primary"` → the
 *  equipped weapon's archetype attack (falling back to the unarmed jab), else a
 *  move clip. */
function selClip(root: THREE.Group, sel: AttackSel): AttackClip | undefined {
  if (sel === "primary") {
    const arch = root.userData.weaponArchetype as WeaponArchetypeId | null;
    return (arch && ARCHETYPE_ANIM[arch]?.attack) || UNARMED.attack;
  }
  return MOVE_CLIPS[sel];
}

/** Merge the per-archetype JSON hold override (ARCHETYPE_ANIM[id].hold, tuned in
 *  the studio) over the item's authored hold. `hilt` is geometry-derived and never
 *  overridden. Shared with validate via `mergeHold` so the two paths cannot drift.
 *  Pass the weapon's `archetype` id (not the item type). */
export function effectiveHold(archetype: WeaponArchetypeId | null, base: Hold): Hold {
  const o = archetype ? ARCHETYPE_ANIM[archetype]?.hold : undefined;
  return mergeHold(base, o ?? null) as Hold;
}

/** How long a swing lasts (seconds) — read from its resolved clip on this rig,
 *  with a fallback for a selector that resolves to no clip. */
export function attackDuration(root: THREE.Group, sel: AttackSel): number {
  return selClip(root, sel)?.duration ?? 0.35;
}

/** Write a static STANCE pose onto the rig: each joint channel the stance defines
 *  becomes that joint's euler; the rest keep the gait pose. `grip` isn't a joint —
 *  the caller reads it to re-angle the weapon (same as a clip's grip channel). */
export function applyStance(rig: RigHandle, pose: StancePose): void {
  for (const ch of JOINT_CHANNELS) {
    const g = rig[ch];
    const v = pose[ch];
    if (g && v) g.rotation.set(v[0], v[1], v[2]);
  }
}

/** The cumulative rotation a joint chain has turned to, parent-then-child
 *  (e.g. shoulder * elbow) — lets a weapon's hold transform be counter-
 *  rotated against a fixed arm-pose override (see seatWeapon). */
const _chainA = new THREE.Quaternion();
const _chainB = new THREE.Quaternion();
function chainQuat(parent: THREE.Group, child: THREE.Group): THREE.Quaternion {
  // Returns a module scratch — consume immediately (seatWeapon copies it),
  // never retain. This runs every frame per armed rig, so it stays alloc-free
  // (the blend.ts discipline).
  return _chainA.setFromEuler(parent.rotation).multiply(_chainB.setFromEuler(child.rotation));
}

/** Seats an equipped weapon's mesh against its `hold` data: `hilt` (= model
 *  `landmarks.grip`) always lands exactly on the hand socket's origin, for ANY
 *  orientation. Pass `armQuat` (the arm's current cumulative rotation, see
 *  chainQuat) when the arm is in a FIXED pose override — that rotation gets
 *  counter-rotated out, so `hold.rot` reads as the model's natural orientation
 *  instead of whatever the override pose happens to point the fist toward.
 *  Omit it for the neutral/gait-following case, where the weapon is meant to
 *  swing naturally along with the arm's own motion.
 *
 *  Orientation layers: item hold.rot (base) → optional clips hold override →
 *  stance/clip grip delta via `rotOverride`. See `src/anim/hold-seat.ts`. */
const _holdE = new THREE.Euler();
const _holdQ = new THREE.Quaternion();
const _seatQ = new THREE.Quaternion(); // separate from _holdQ/_chainA: armQuat may BE the chainQuat scratch
const _hilt = new THREE.Vector3();
export function seatWeapon(mesh: THREE.Group, h: Hold, armQuat?: THREE.Quaternion, rotOverride?: Vec3): void {
  const r = rotOverride ?? h.rot;
  _holdQ.setFromEuler(_holdE.set(r[0], r[1], r[2]));
  if (armQuat) mesh.quaternion.copy(_seatQ.copy(armQuat).invert().multiply(_holdQ));
  else mesh.quaternion.copy(_holdQ);
  const scale = h.scale ?? 1;
  if (h.scale) mesh.scale.setScalar(scale);
  // Rotate the hilt point by the mesh's (possibly counter-rotated) orientation,
  // then negate — the unique translation that puts that exact local point back
  // at the hand socket's origin (0,0,0 in handMain), for ANY orientation.
  _hilt.set(h.hilt[0] * scale, h.hilt[1] * scale, h.hilt[2] * scale).applyQuaternion(mesh.quaternion);
  mesh.position.set(-_hilt.x, -_hilt.y, -_hilt.z);
  if (h.offset) {
    mesh.position.x += h.offset[0];
    mesh.position.y += h.offset[1];
    mesh.position.z += h.offset[2];
  }
}

/** Equip (or clear, with spec=null) a weapon on any rig with a handMain socket:
 *  parents the item's model into the main hand with the spec's hold transform.
 *  Used by the local player, remote players (net.ts), and dressed NPCs. */
export function equipRig(root: THREE.Group, rig: RigHandle, spec: ItemSpec | null): void {
  const prev = root.userData.weaponMesh as THREE.Group | null;
  if (prev) { prev.parent?.remove(prev); disposeGroup(prev); } // free the old weapon's geometry (leaked on every swap)
  root.userData.weaponMesh = null;
  root.userData.weaponSpec = null;
  root.userData.weaponType = null;
  root.userData.weaponArchetype = null;
  // A weapon takes the main hand back from any held tool (plan 10 — the server
  // enforces the same weapon/tool exclusivity on the wire).
  if (spec) {
    const prevTool = root.userData.toolMesh as THREE.Group | null;
    if (prevTool) { prevTool.parent?.remove(prevTool); disposeGroup(prevTool); }
    root.userData.toolMesh = null;
    root.userData.toolSpec = null;
  }
  // Zero every joint the rig publishes: stances/clips set full eulers on axes the
  // gait never rewrites (shoulder.y, elbow.y/z), so without this the last stance's
  // cocked-elbow residue outlives the weapon after an unequip until a re-equip. The
  // next gait frame re-poses the animated axes, so this is invisible while armed.
  for (const ch of JOINT_CHANNELS) rig[ch]?.rotation.set(0, 0, 0);
  if (!spec || !spec.weapon || !rig.handMain) return;
  const mesh = buildItem(spec);
  seatWeapon(mesh, effectiveHold(spec.weapon.archetype, spec.weapon.hold));
  rig.handMain.add(mesh);
  root.userData.weaponMesh = mesh;
  root.userData.weaponSpec = spec.weapon;
  root.userData.weaponType = spec.type; // equipped item type (mesh / studio preview)
  root.userData.weaponArchetype = spec.weapon.archetype; // anim lookup key
}

/** Equip (or clear, with spec=null) an offhand SHIELD (plan 14) on any rig with
 *  a handOff socket: seats the item's model in the off fist with the shield's
 *  hold transform. The spec is remembered on userData.shieldSpec so a redress
 *  (gear change rebuilds the arms) can re-seat it without the caller's help. */
export function equipOffhand(root: THREE.Group, rig: RigHandle, spec: ItemSpec | null): void {
  const prev = root.userData.shieldMesh as THREE.Group | null;
  if (prev) { prev.parent?.remove(prev); disposeGroup(prev); }
  root.userData.shieldMesh = null;
  root.userData.shieldSpec = spec && spec.shield ? spec : null;
  if (!spec || !spec.shield || !rig.handOff) return;
  const mesh = buildItem(spec);
  seatWeapon(mesh, spec.shield.hold);
  rig.handOff.add(mesh);
  root.userData.shieldMesh = mesh;
}

/** Equip (or clear, with spec=null) a held TOOL (fishing rod or hatchet) into
 *  the handMain socket. Mirrors equipOffhand: seats the item's model with the
 *  tool hold transform and stashes it on userData.toolSpec so a redress can
 *  re-seat it. Taking the tool also clears any equipped weapon (main-hand
 *  exclusivity — equipRig clears the tool symmetrically).
 *
 *  Hatchets also wire the weapon-pose system (`weaponArchetype = "hatchet"`,
 *  synthetic weaponSpec) so carry/ready/attack clips drive chopping. Rods
 *  leave the pose system alone and ride the gait + applyFishingPose. */
export function equipTool(root: THREE.Group, rig: RigHandle, spec: ItemSpec | null): void {
  const prev = root.userData.toolMesh as THREE.Group | null;
  if (prev) { prev.parent?.remove(prev); disposeGroup(prev); }
  // If the previous tool was a hatchet, clear the pose-system aliases (mesh
  // lived only on toolMesh — never share with weaponMesh or equipRig double-disposes).
  if ((root.userData.toolSpec as ItemSpec | null)?.chopping) {
    root.userData.weaponSpec = null;
    root.userData.weaponArchetype = null;
    root.userData.weaponType = null;
  }
  root.userData.toolMesh = null;
  root.userData.toolSpec = null;
  if (!spec || !rig.handMain) return;

  if (spec.fishing) {
    equipRig(root, rig, null);
    const mesh = buildItem(spec);
    seatWeapon(mesh, spec.fishing.hold);
    rig.handMain.add(mesh);
    root.userData.toolMesh = mesh;
    root.userData.toolSpec = spec;
    return;
  }

  if (spec.chopping) {
    equipRig(root, rig, null);
    const mesh = buildItem(spec);
    const hold = effectiveHold("hatchet", spec.chopping.hold);
    seatWeapon(mesh, hold);
    rig.handMain.add(mesh);
    root.userData.toolMesh = mesh;
    root.userData.toolSpec = spec;
    // Wire pose system so applyWeaponPose + startAttack drive the hatchet.
    // Mesh stays on toolMesh only (see applyWeaponPose mesh fallback).
    root.userData.weaponType = spec.type;
    root.userData.weaponArchetype = "hatchet";
    root.userData.weaponSpec = {
      archetype: "hatchet",
      damage: 0,
      attackCooldown: 0.5,
      staminaCost: 1,
      attackRange: 2.2,
      grip: "one-hand",
      style: "chop",
      hold: spec.chopping.hold,
    } satisfies WeaponSpec;
    return;
  }
}

/** The hold-to-block pose (plan 14): raise the OFF arm across the chest. A
 *  transient per-frame override — call it AFTER the gait/stance pass so it
 *  wins (same ordering rule as attack clips). The handOff arm is the R chain
 *  (the rig-handedness gotcha: handMain rides shoulderL/elbowL). */
export function applyBlockPose(rig: RigHandle): void {
  rig.shoulderR?.rotation.set(-1.15, 0.35, 0);
  rig.elbowR?.rotation.set(-0.55, 0, 0);
}

/** The two-handed fishing pose (plan 10): while a line is out (game.fishing) the
 *  hero grips the rod with BOTH hands and holds it up toward the water. A static
 *  per-frame override, applied AFTER the gait so it wins — same rule as the block
 *  / reading poses. The rod is a held TOOL parented to handMain (the shoulderL/
 *  elbowL chain — the rig-handedness gotcha), seated once with a fixed forward
 *  lean; here we only pose the arms so the rod reads as raised-and-forward and
 *  bring the off hand (shoulderR/elbowR) across to grip the shaft a little above
 *  the main hand. Movement is locked while fishing, so no gait sway rides on top. */
export function applyFishingPose(rig: RigHandle): void {
  // Main (rod) arm: shoulder set back + forearm flexed up brings the fist to the
  // waist in front of the belly with the rod angled up-and-forward (~50°) toward
  // the cast — the natural "waiting for a bite" hold.
  rig.shoulderL?.rotation.set(0.55, 0.4, 0.2);
  rig.elbowL?.rotation.set(-0.7, 0, 0);
  // Off arm: reach across the body so the fist meets the shaft a little way
  // above the main grip — the two hands stack along the pole (a real two-handed
  // hold), main hand low by the reel, off hand up the handle.
  rig.shoulderR?.rotation.set(-0.65, -0.8, -0.9);
  rig.elbowR?.rotation.set(-1.0, 0, 0);
  // A touch of forward lean + eyes down the line.
  rig.torso?.rotation.set(0.12, 0.05, 0);
  rig.head?.rotation.set(0.15, 0, 0);
}

/** Kick off an attack swing of the given selector — overrides the arm's gait pose
 *  for the clip's duration (applyWeaponPose plays it out). */
export function startRigAttack(root: THREE.Group, sel: AttackSel): void {
  root.userData.attackStyle = sel;
  root.userData.attackT = attackDuration(root, sel);
  root.userData.attackFreeze = null; // a real swing plays out on its timer
}

/** Optional locomotion overlay on carry stances — pass the current gait swing
 *  (sin of phase) + arm amplitude (profile × walkBlend, larger when running).
 *  Used for one-hand / bow free-arm sway and 2H coupled upper-body rock
 *  (`archetypes.<id>.carrySway` in clips.json). */
export type CarrySway = { swing: number; armAmp: number };

/** The weapon pose pass — run AFTER the gait so its writes win. Two layers:
 *  the held/ready stance while carrying (not mid-swing), then the active swing
 *  clip on top. MOVED verbatim from player.ts updatePlayer. */
export function applyWeaponPose(root: THREE.Group, rig: RigHandle, dt: number, sway?: CarrySway): void {
  const spec = root.userData.weaponSpec as WeaponSpec | null;
  // Hatchets seat on toolMesh (held-tool channel) while still driving this pose
  // pass via weaponSpec/weaponArchetype — fall back so seating still works.
  const mesh = (root.userData.weaponMesh ?? root.userData.toolMesh) as THREE.Group | null;
  // equipRig sets weaponSpec + weaponArchetype together, so this is the single
  // source of truth for the equipped archetype (null when unarmed).
  const arch = root.userData.weaponArchetype as WeaponArchetypeId | null;
  const hold = spec ? effectiveHold(arch, spec.hold) : null;
  const ready = (root.userData.combatReady as boolean) ?? false;
  let attackT = (root.userData.attackT as number) ?? 0;
  // `attackFreeze` (studio scrubbing): a fixed 0→1 clip time that holds the pose
  // open without the timer running. Null/undefined in-game, where attackT ticks.
  const freeze = root.userData.attackFreeze as number | null | undefined;
  const frozen = freeze != null;
  const mid = frozen || attackT > 0;

  // Hold/ready stance — applied ALWAYS when a stance exists (even mid-swing), so
  // the swing clip has a correct pose to BLEND against as it fades in/out
  // (blend.ts). The carry↔ready switch itself EASES (no pop) via a captured
  // from-pose. Armed stances are data-driven (ARCHETYPE_ANIM[arch]); unarmed uses
  // UNARMED.ready (the boxer guard) when combatReady — no weapon hold to seat.
  // Seating + walking-gait overlay run only when NOT mid-swing (armed only).
  {
    const twoHand = !!(spec && hold && spec.grip === "two-hand");
    const wa = (spec && hold && arch) ? ARCHETYPE_ANIM[arch] : null;
    // Unarmed: only the combat-ready guard (no idle carry — gait owns the arms).
    const stance: StancePose | undefined = wa
      ? (ready ? wa.ready : wa.carry)
      : (ready ? UNARMED.ready : undefined);
    const carryPose = wa?.carry ?? undefined;
    const readyPose = wa?.ready ?? UNARMED.ready;

    // Stance ease: capture the pose we're LEAVING on a combatReady flip, then ramp
    // toward the new stance over stanceFade (so carry↔ready / idle↔guard doesn't pop).
    if (wa || UNARMED.ready) {
      const prevReady = root.userData.__stanceReady as boolean | undefined;
      if (prevReady === undefined) root.userData.__stanceReady = ready;
      else if (prevReady !== ready) {
        const leaving = ready ? carryPose : readyPose; // the stance we're leaving
        const from: Record<string, Vec3> = {};
        captureStanceFrom(rig, leaving, stanceChannels(carryPose, readyPose), from);
        root.userData.__stanceFrom = from;
        root.userData.__stanceBlend = 0;
        root.userData.__stanceReady = ready;
      }
    }
    let sb = (root.userData.__stanceBlend as number | undefined) ?? 1;
    if (sb < 1 && !frozen) { sb = Math.min(1, sb + dt / BLEND_TUNING.stanceFade); root.userData.__stanceBlend = sb; }

    if (stance) {
      const from = root.userData.__stanceFrom as Record<string, Vec3> | undefined;
      if (sb < 1 && from) applyStanceBlended(rig, from, stance, sb);
      else applyStance(rig, stance);
    }

    if (spec && hold && !mid) {
      // Locomotion overlay on the stance (gait torso/arm writes were overwritten
      // above). Carry gets full arm + torso rock; combat-ready keeps the weapon
      // arm(s) planted but still applies a lighter torso/head bounce when
      // carrySway is set so walk/run in guard doesn't look stiff.
      if (stance && sway && sway.armAmp > 0.001) {
        const a = Math.min(1.2, sway.armAmp); // walk~0.6, run~1.2 (player profile)
        // One-hand carry + bow side-carry: free/main arm rides a fraction of the
        // gait swing. Bows amplify torso twist so the upper body reads clearly.
        const sideCarry = !twoHand || arch === "bow";
        const cs = wa?.carrySway && wa.carrySway !== false ? wa.carrySway : null;
        if (!ready) {
          if (sideCarry) {
            // Free/main-arm gait fraction. Archetypes with carrySway (e.g. battleaxe)
            // get a stronger arm + full torso/head rock so walk/run reads clearly.
            const mainMul = arch === "bow" ? 0.4 : cs ? 0.42 : 0.3;
            const elbowMul = arch === "bow" ? 0.28 : cs ? 0.26 : 0.2;
            if (rig.shoulderL) rig.shoulderL.rotation.x -= sway.swing * sway.armAmp * mainMul;
            if (rig.elbowL) rig.elbowL.rotation.x -= Math.max(0, -sway.swing) * sway.armAmp * elbowMul;
            if (cs) {
              // Data-driven upper-body rock (same knobs as 2H carrySway).
              const torsoY = cs.torsoY ?? 0.055;
              const torsoZ = cs.torsoZ ?? 0.035;
              const torsoX = cs.torsoX ?? 0.02;
              const headY = cs.headY ?? 0.028;
              const headZ = cs.headZ ?? 0.014;
              if (rig.torso) {
                rig.torso.rotation.y += sway.swing * torsoY * a;
                rig.torso.rotation.z += sway.swing * torsoZ * a;
                rig.torso.rotation.x += Math.abs(sway.swing) * torsoX * a;
              }
              // Off-arm counter-sway (free hand) so the upper body isn't one-sided.
              if (rig.shoulderR) {
                rig.shoulderR.rotation.x += sway.swing * sway.armAmp * (cs.rockMul ?? 0.12);
              }
              if (rig.elbowR) {
                rig.elbowR.rotation.x -= Math.max(0, sway.swing) * sway.armAmp * (cs.elbowMul ?? 0.08);
              }
              if (rig.head) {
                rig.head.rotation.y -= sway.swing * headY * a;
                rig.head.rotation.z -= sway.swing * headZ * a;
              }
            } else if (arch === "bow" && rig.torso) {
              rig.torso.rotation.y += sway.swing * 0.05 * Math.min(1, a);
              rig.torso.rotation.z += sway.swing * 0.03 * Math.min(1, a);
            }
          } else if (twoHand && cs) {
            // Two-hand carry: data-driven upper-body rock (content/anim/clips.json
            // archetypes.<id>.carrySway). Hands move as a UNIT so dual grip doesn't
            // shear; torso twist/roll + head counter keep the weapon feeling carried.
            // `carrySway: false` or omit = locked (no free arm sway either).
            const torsoY = cs.torsoY ?? 0.055;
            const torsoZ = cs.torsoZ ?? 0.035;
            const torsoX = cs.torsoX ?? 0.02;
            const rockMul = cs.rockMul ?? 0.12;
            const elbowMul = cs.elbowMul ?? 0.08;
            const headY = cs.headY ?? 0.028;
            const headZ = cs.headZ ?? 0.014;
            if (rig.torso) {
              rig.torso.rotation.y += sway.swing * torsoY * a;
              rig.torso.rotation.z += sway.swing * torsoZ * a;
              // Double-frequency pitch bob on footfalls (two peaks per stride).
              rig.torso.rotation.x += Math.abs(sway.swing) * torsoX * a;
            }
            const rock = sway.swing * sway.armAmp * rockMul;
            if (rig.shoulderL) rig.shoulderL.rotation.x -= rock;
            if (rig.shoulderR) rig.shoulderR.rotation.x -= rock;
            // Light elbow give on the back half of the rock (both arms, same phase).
            const elbowRock = Math.max(0, -sway.swing) * sway.armAmp * elbowMul;
            if (rig.elbowL) rig.elbowL.rotation.x -= elbowRock;
            if (rig.elbowR) rig.elbowR.rotation.x -= elbowRock;
            if (rig.head) {
              rig.head.rotation.y -= sway.swing * headY * a;
              rig.head.rotation.z -= sway.swing * headZ * a;
            }
          }
          // 2H without carrySway (or carrySway:false) stays locked to carry
        } else if (cs) {
          // Combat-ready walk/run: weapon arm(s) stay on guard; torso/head (and
          // 1H free hand) bounce at ~half carrySway so the upper body still
          // reads locomotion without wobbling the blade.
          const r = 0.55;
          const torsoY = (cs.torsoY ?? 0.055) * r;
          const torsoZ = (cs.torsoZ ?? 0.035) * r;
          const torsoX = (cs.torsoX ?? 0.02) * r;
          const headY = (cs.headY ?? 0.028) * r;
          const headZ = (cs.headZ ?? 0.014) * r;
          if (rig.torso) {
            rig.torso.rotation.y += sway.swing * torsoY * a;
            rig.torso.rotation.z += sway.swing * torsoZ * a;
            rig.torso.rotation.x += Math.abs(sway.swing) * torsoX * a;
          }
          if (rig.head) {
            rig.head.rotation.y -= sway.swing * headY * a;
            rig.head.rotation.z -= sway.swing * headZ * a;
          }
          if (sideCarry) {
            if (rig.shoulderR) {
              rig.shoulderR.rotation.x += sway.swing * sway.armAmp * (cs.rockMul ?? 0.12) * r;
            }
            if (rig.elbowR) {
              rig.elbowR.rotation.x -= Math.max(0, sway.swing) * sway.armAmp * (cs.elbowMul ?? 0.08) * r;
            }
          }
        }
      }
      if (mesh) {
        // A stance may also carry a `grip` delta (lay the blade differently in the fist).
        const g = stance?.grip;
        const rot: Vec3 | undefined = g
          ? [hold.rot[0] + g[0], hold.rot[1] + g[1], hold.rot[2] + g[2]]
          : undefined;
        // Two-hander seats RAW (fused to the hand); one-hander counter-rotates the
        // arm out (chainQuat) so hold.rot reads as the natural in-fist orientation.
        if (twoHand || !rig.shoulderL || !rig.elbowL) seatWeapon(mesh, hold, undefined, rot);
        else seatWeapon(mesh, hold, chainQuat(rig.shoulderL, rig.elbowL), rot);
      }
    }
  }

  // Active swing — the style's tuning CLIP, BLENDED over the stance/gait by an
  // envelope weight so the start and end don't snap (blend.ts). The synthetic
  // `grip` delta is scaled by the same weight so the blade eases forward with the
  // arm. The weapon re-seats RAW (fused to the hand) mid-swing.
  if (mid) {
    const sel = (root.userData.attackStyle as AttackSel) ?? "primary";
    const clip = selClip(root, sel);
    // Tick the timer FIRST, independent of whether a clip exists — attackDuration
    // falls back to 0.35s for a selector with no clip, so keeping the countdown inside
    // `if (clip)` would leave such a swing stuck mid-attack forever.
    if (!frozen) {
      attackT = Math.max(0, attackT - dt);
      root.userData.attackT = attackT;
    }
    if (clip) {
      const k = frozen
        ? Math.max(0, Math.min(1, freeze!)) // studio holds the pose at this time
        : 1 - attackT / clip.duration;      // 0 → 1 as the swing plays out
      const w = frozen ? 1 : attackEnvelope(k, clip.duration); // studio scrub shows the true clip
      applyClipBlended(rig, clip, k, w);
      if (mesh && hold) {
        const grip = sampleChannel(clip, "grip", k);
        const rot: Vec3 | undefined = grip
          ? [hold.rot[0] + grip[0] * w, hold.rot[1] + grip[1] * w, hold.rot[2] + grip[2] * w]
          : undefined;
        seatWeapon(mesh, hold, undefined, rot);
      }
    }
  }
}
